// Réordonnancement par glisser-déposer des miniatures de la zone « Images ».
//
// La zone d'upload de la fiche produit (vue2-dropzone) affiche les miniatures
// dans l'ordre du tableau form.images : c'est cet ordre qui donne l'image
// envoyée aux collections (FirstImage = images[0].src, lu par
// product-grid.init.js, RelatedProducts.ejs et l'accueil) et l'ordre de la
// galerie de la fiche produit. Dropzone ne sait pas trier ses previews, on
// ajoute donc un glisser-déposer HTML5 natif par-dessus, sans dépendance.
//
// Les previews sont créés et détruits par Dropzone, hors de Vue : le tri
// fonctionne donc en délégation d'événements + MutationObserver, et les styles
// sont globaux (importés ici) car un CSS « scoped » n'atteindrait pas ces
// éléments dépourvus de l'attribut data-v-*.
import './../assets/css/image-order.css';

const PREVIEW = '.dz-preview';
const BADGE = 'image-order-badge';

export function enableImageOrder(container, options) {
  const settings = options || {};
  if (!container || !container.appendChild) return null;
  // Idempotence : un second appel sur le même conteneur ne ré-attache pas
  // d'écouteurs, il rafraîchit l'ordre affiché.
  if (container.__imageOrder) {
    container.__imageOrder.refresh();
    return container.__imageOrder;
  }

  const previews = () => Array.prototype.slice.call(container.querySelectorAll(PREVIEW));
  const files = () => (typeof settings.getFiles === 'function' ? settings.getFiles() || [] : []);

  // Nom de fichier porté par une miniature : la référence previewElement de
  // Dropzone fait foi, l'élément [data-dz-name] sert de repli.
  const nameOf = (el) => {
    const list = files();
    for (let i = 0; i < list.length; i++) {
      if (list[i] && list[i].previewElement === el) return list[i].name;
    }
    const node = el.querySelector('[data-dz-name]');
    return node ? node.textContent : null;
  };

  let dragged = null;

  const markDraggable = () => {
    previews().forEach((el) => {
      el.classList.add('image-order-item');
      if (el.getAttribute('draggable') !== 'true') el.setAttribute('draggable', 'true');
      if (!el.getAttribute('title')) el.setAttribute('title', 'Drag to reorder the images');
    });
  };

  // La première miniature est celle reprise par les collections : elle est
  // numéro 1 et reçoit un libellé explicite, les autres leur rang.
  const markMain = () => {
    previews().forEach((el, index) => {
      el.classList.toggle('image-order-first', index === 0);
      let badge = el.querySelector('.' + BADGE);
      if (!badge) {
        badge = document.createElement('span');
        badge.className = BADGE;
        el.insertBefore(badge, el.firstChild);
      }
      badge.classList.toggle('is-main', index === 0);
      badge.textContent = index === 0 ? (settings.mainLabel || 'Main image') : String(index + 1);
    });
  };

  // Remonte au composant l'ordre courant des miniatures.
  const commit = () => {
    markDraggable();
    markMain();
    if (typeof settings.onOrder === 'function') settings.onOrder(previews().map(nameOf));
  };

  const previewAt = (x, y) => {
    const list = previews();
    for (let i = 0; i < list.length; i++) {
      if (list[i] === dragged) continue;
      const r = list[i].getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return list[i];
    }
    return null;
  };

  // Les miniatures sont en inline-block (grille) : on déplace réellement
  // l'élément pendant le survol, ce qui sert de retour visuel.
  const insertNear = (target, x, y) => {
    const r = target.getBoundingClientRect();
    const sameRow = y >= r.top && y <= r.bottom;
    const after = sameRow ? x > r.left + r.width / 2 : y > r.top + r.height / 2;
    container.insertBefore(dragged, after ? target.nextSibling : target);
  };

  const endDrag = () => {
    if (dragged) dragged.classList.remove('image-order-dragging');
    dragged = null;
    commit();
  };

  const onDragStart = (e) => {
    const el = e.target && e.target.closest ? e.target.closest(PREVIEW) : null;
    if (!el || !container.contains(el)) return;
    dragged = el;
    el.classList.add('image-order-dragging');
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      try {
        e.dataTransfer.setData('text/plain', nameOf(el) || '');
      } catch (err) {
        // Certains navigateurs refusent setData pendant dragstart : sans
        // conséquence, le déplacement est piloté par dragover.
      }
    }
  };

  // Phase capture : on traite l'événement avant Dropzone, qui écoute en
  // bubbling sur ce même conteneur. Sans cela un déplacement interne serait
  // pris pour un dépôt de fichier venu de l'explorateur. À l'inverse, quand
  // aucun drag interne n'est en cours (dragged === null) on ne fait rien et
  // l'upload par glisser-déposer continue de fonctionner normalement.
  const onCaptureEvent = (e) => {
    if (!dragged) return;
    if (e.type === 'dragover') {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      const target = previewAt(e.clientX, e.clientY);
      if (target) insertNear(target, e.clientX, e.clientY);
    } else if (e.type === 'dragenter') {
      e.preventDefault();
      e.stopPropagation();
    } else if (e.type === 'drop') {
      e.preventDefault();
      e.stopPropagation();
      endDrag();
    }
  };

  const CAPTURED = ['dragenter', 'dragover', 'drop'];
  container.addEventListener('dragstart', onDragStart);
  container.addEventListener('dragend', endDrag);
  CAPTURED.forEach((type) => container.addEventListener(type, onCaptureEvent, true));

  // Ajout (upload) ou retrait d'une miniature : rangs et image principale sont
  // mis à jour, et l'ordre est resynchronisé avec form.images. childList seul
  // (sans subtree) : les badges ajoutés dans un preview ne ré-déclenchent rien.
  let observer = null;
  if (typeof MutationObserver === 'function') {
    observer = new MutationObserver(() => commit());
    observer.observe(container, { childList: true });
  }

  container.__imageOrder = {
    refresh: commit,
    destroy() {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      container.removeEventListener('dragstart', onDragStart);
      container.removeEventListener('dragend', endDrag);
      CAPTURED.forEach((type) => container.removeEventListener(type, onCaptureEvent, true));
      delete container.__imageOrder;
    }
  };

  commit();
  return container.__imageOrder;
}

export default enableImageOrder;
